<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup';
import { useForm } from 'vee-validate';
import { computed, nextTick, ref, useTemplateRef, watch, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { array, boolean, number, object, string } from 'yup';

import FieldInput from '@/components/forms/controls/FieldInput.vue';
import FieldPhone from '@/components/forms/controls/FieldPhone.vue';
import FieldTextarea from '@/components/forms/controls/FieldTextarea.vue';
import { useUnitsQuoteApi } from '@/composables/useUnitsQuoteApi.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { NewQuoteInfo } from '@/typings/quotes/NewQuotesInfo.ts';
import type { SendQuoteInfoInquiryRequest } from '@/typings/requests/SendQuoteInfoInquiry.ts';
import { onInvalidSubmit } from '@/utils/formErrorHandling.ts';
import { getTrackingValues } from '@/utils/trackingValues.ts';

const MAX_NAME_LENGTH = 50;
const MAX_EMAIL_LENGTH = 100;
const MAX_PHONE_LENGTH = 10;
const MAX_ADDITIONAL_INFO_LENGTH = 250;

const { t } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});
const { sendQuoteInfoInquiry, sendQuoteInfoInquiryLoading } = useUnitsQuoteApi();
const submissionMessagesElement = useTemplateRef<HTMLDivElement>('submissionMessagesElement');

const model = defineModel<SendQuoteInfoInquiryRequest>({
  default: () => ({
    unitId: 0,
    name: '',
    email: '',
    phone: undefined,
    additionalInfo: undefined,
    selectedConfigurationId: undefined,
    selectedStyleId: undefined,
    selectedOptionIds: [],
    isEligibleFirstTimeHomeBuyer: false
  })
});

const props = defineProps<{
  quoteInfo: NewQuoteInfo,
  defaultAdditionnalInfo?: string
  nested?: boolean,
  selectedConfigurationId?: number,
  selectedStyleId?: number,
  selectedOptionIds?: number[],
  isFirstTimeHomebuyer?: boolean
}>();

const schema = toTypedSchema(
  object({
    unitId: number().required().default(() => props.quoteInfo.unit.id),
    name: string()
      .required(t('validation.required'))
      .max(MAX_NAME_LENGTH, t('validation.maxLength', { max: MAX_NAME_LENGTH }))
      .label(t('forms.labels.name')),
    
    email: string()
      .required(t('validation.required'))
      .email(t('validation.email'))
      .max(MAX_EMAIL_LENGTH, t('validation.maxLength', { max: MAX_EMAIL_LENGTH }))
      .label(t('forms.labels.email')),
    
    phone: string()
      .optional()
      .test('phone-digits', t('validation.phoneDigits'),
        value => !value || (value.replace(/\D/g, '').length === MAX_PHONE_LENGTH))
      .label(t('forms.labels.telephone')),
    
    additionalInfo: string()
      .optional()
      .label(t('forms.labels.additionalInformation'))
      .default(props.defaultAdditionnalInfo)
      .max(MAX_ADDITIONAL_INFO_LENGTH, t('validation.maxLength', { max: MAX_ADDITIONAL_INFO_LENGTH })),
    
    selectedConfigurationId: number().optional().default(() => props.selectedConfigurationId),
    selectedStyleId: number().optional().default(() => props.selectedStyleId),
    selectedOptionIds: array().of(number()).optional().default(() => props.selectedOptionIds),
    isEligibleFirstTimeHomeBuyer: boolean().optional().default(() => props.isFirstTimeHomebuyer ?? false),
    trackingValues: string().optional().default(() => getTrackingValues())
  })
);

const { handleSubmit, values, resetForm, setFieldValue } = useForm<SendQuoteInfoInquiryRequest>({
  validationSchema: schema
});
const formElement = useTemplateRef<HTMLFormElement>('formElement');

// Synchroniser les props avec les valeurs du formulaire
watch(() => props.selectedConfigurationId, (newValue) => {
  setFieldValue('selectedConfigurationId', newValue);
}, { immediate: true });

watch(() => props.selectedStyleId, (newValue) => {
  setFieldValue('selectedStyleId', newValue);
}, { immediate: true });

watch(() => props.selectedOptionIds, (newValue) => {
  setFieldValue('selectedOptionIds', newValue || []);
}, { immediate: true, deep: true });

watch(() => props.isFirstTimeHomebuyer, (newValue) => {
  setFieldValue('isEligibleFirstTimeHomeBuyer', newValue ?? false);
}, { immediate: true });

const emit = defineEmits<{
  'submit:success': [SendQuoteInfoInquiryRequest],
  'submit:error': [string]
}>();

const submissionFailed = ref(false);

const showActions = computed(() => {
  return !props.nested;
});

async function onSubmitForm() {
  submissionFailed.value = false;

  handleSubmit(async values => {
    if (!formElement.value) {
      return;
    }

    try {
      values.trackingValues = getTrackingValues();
      await sendQuoteInfoInquiry(values);
      emit('submit:success', values);
      resetForm();
    } catch (error) {
      emit('submit:error', error as string);
      submissionFailed.value = true;
      nextTick(() => {
        if (submissionMessagesElement.value) {
          submissionMessagesElement.value.scrollIntoView({
            behavior: 'smooth',
            block: 'end'
          });
        }
      });
    }
  },
  () => {
    if(!formElement.value) {
      return;
    }

    onInvalidSubmit(formElement.value);
  })();
};

function onResetForm() {
  resetForm();
  submissionFailed.value = false;
}

defineExpose({
  onResetForm,
  onSubmitForm,
  sendQuoteInfoInquiryLoading
});

watchEffect(() => {
  model.value = {
    ...values,
  };
});
</script>

<template>
  <form ref="formElement"
        class="standardForm"
        @submit.prevent="onSubmitForm">
    <FieldInput name="name"
                :label="t('forms.labels.name')"
                :max-length="MAX_NAME_LENGTH"
                required />
    
    <FieldInput name="email"
                :label="t('forms.labels.email')"
                type="email"
                :max-length="MAX_EMAIL_LENGTH"
                required />
    
    <FieldPhone name="phone"
                :label="t('forms.labels.telephone')" />
    
    <FieldTextarea name="additionalInfo"
                   :label="t('forms.labels.additionalInformation')" 
                   :hint="t('forms.hints.maxCharacterCount', { max: MAX_ADDITIONAL_INFO_LENGTH })"
                   :max-length="MAX_ADDITIONAL_INFO_LENGTH"
                   :rows="4" />
    <div v-if="submissionFailed"
         ref="submissionMessagesElement"
         class="standardForm-submissionMessages">
      <span class="standardForm-errorMessage">
        {{ t('forms.errors.submissionError') }}
      </span>
    </div>
      
    <div v-if="showActions"
         class="standardForm-actions">
      <button type="submit"
              class="btn btn_dark">
        {{ t('forms.labels.submitMyInquiry', 'Soumettre') }}
      </button>
    </div>
  </form>
</template>

<style lang="scss">
.form-actions {
  margin-top: 1.5rem;
  text-align: right;
}
</style>
